import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-level cache shared across every <Img> on the page.
let _cache = null;       // map: { key: { url, alt } }
let _inflight = null;
const _subscribers = new Set();

function _broadcast(value) {
  _cache = value;
  _subscribers.forEach((fn) => fn(value));
}

async function _fetchOnce() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = axios.get(`${API}/images`)
    .then((res) => {
      _broadcast(res.data || {});
      return _cache;
    })
    .catch(() => {
      _broadcast({});
      return _cache;
    })
    .finally(() => { _inflight = null; });
  return _inflight;
}

/**
 * Subscribe to the global image-override map. Returns `{url, alt}` for one
 * key, falling back to (defaultUrl, defaultAlt) when no override exists.
 *
 * Safe to call before the fetch completes — components re-render automatically
 * when the override map arrives.
 */
export function useImage(key, defaultUrl, defaultAlt = "") {
  const [map, setMap] = useState(_cache || {});

  useEffect(() => {
    _subscribers.add(setMap);
    if (!_cache) _fetchOnce();
    return () => { _subscribers.delete(setMap); };
  }, []);

  const override = map?.[key];
  return {
    url: override?.url || defaultUrl,
    alt: override?.alt || defaultAlt,
    isOverride: !!override?.url,
  };
}

/** Used by admin panel after a successful save so every page-mounted <Img> updates immediately. */
export function setImageOverride(key, value) {
  const next = { ...(_cache || {}) };
  if (value && value.url) next[key] = { url: value.url, alt: value.alt || "" };
  else delete next[key];
  _broadcast(next);
}

export function refreshImages() {
  _cache = null;
  return _fetchOnce();
}
